import React from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { BarChart3, DollarSign, LayoutGrid, Home, LogOut, Info, MessageSquare, Wallet } from "lucide-react";
import { Link } from "react-router-dom";

interface DesktopNavigationProps {
  className?: string;
}

export const DesktopNavigation: React.FC<DesktopNavigationProps> = ({ className }) => {
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const navItems = [
    {
      href: "/dashboard",
      icon: <Home className="w-6 h-6" />,
      label: t("navigation.home"),
      isActive: pathname === "/dashboard",
    },
    {
      href: "/expenses/new",
      icon: <DollarSign className="w-6 h-6" />,
      label: t("navigation.expenses"),
      isActive: pathname === "/expenses/new",
    },
    {
      href: "/wallet",
      icon: <Wallet className="w-6 h-6" />,
      label: t("navigation.wallet"),
      isActive: pathname === "/wallet",
    },
    {
      href: "/movements",
      icon: <LayoutGrid className="w-6 h-6" />,
      label: t("navigation.movements"),
      isActive: pathname === "/movements",
    },
    {
      href: "/analysis",
      icon: <BarChart3 className="w-6 h-6" />,
      label: t("navigation.analysis"),
      isActive: pathname === "/analysis",
    },
    {
      href: "/chat",
      icon: <MessageSquare className="w-6 h-6" />,
      label: t("navigation.chat"),
      isActive: pathname === "/chat",
    },
  ];

  return (
    <div
      className={cn("hidden h-full overflow-x-hidden overflow-y-scroll sm:flex min-w-[288px] rounded-lg", className)}
    >
      <div className="relative flex h-full w-72 flex-col bg-slate-800/50 backdrop-blur p-6">
        {/* App Logo & Name */}
        <div className="flex items-center gap-3 pl-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/10">
            <DollarSign className="h-5 w-5 text-white" />
          </div>
          <span className="text-sm font-bold uppercase text-white">{t("layout.appName")}</span>
        </div>

        {/* Profile Section */}
        <div className="mt-6 flex items-center gap-3 px-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-700 ring-2 ring-white/10">
            <span className="text-sm font-medium text-white">U</span>
          </div>
          <div className="flex max-w-full flex-col">
            <p className="text-sm font-medium text-white">User</p>
            <p className="text-xs text-slate-400">Personal Account</p>
          </div>
        </div>

        <span aria-hidden="true" className="my-6 block h-px w-full bg-slate-700/50" />

        {/* Navigation Items */}
        <nav className="flex flex-col gap-1">
          {navItems.map(item => (
            <Link
              key={item.href}
              to={item.href}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                "hover:bg-slate-700/50",
                item.isActive ? "bg-slate-700/50 text-white" : "text-slate-400 hover:text-white"
              )}
            >
              <span className={cn("text-slate-400", item.isActive && "text-white")}>{item.icon}</span>
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="mt-auto flex flex-col gap-2">
          <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 hover:bg-slate-700/50 hover:text-white transition-colors">
            <Info className="h-6 w-6" />
            Support
          </button>
          <button className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-slate-400 hover:bg-slate-700/50 hover:text-white transition-colors">
            <LogOut className="h-6 w-6" />
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
};
